import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  useMediaQuery,
  Skeleton,
  CircularProgress,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const FollowingId = () => {
  const [resellers, setResellers] = useState([]);
  const [followingIds, setFollowingIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const username = localStorage.getItem('username');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchFollowingResellers = async () => {
      try {
        setLoading(true);

        const followingRes = await fetch('https://reviewhub-lc.onrender.com/api/auth/get-following', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username }),
        }).then(res => res.json());

        const followingList = followingRes.followingResellers || [];
        setFollowingIds(followingList);

        const allResellers = await fetch('https://reviewhub-lc.onrender.com/api/resellers/all')
          .then(res => res.json());

        const filtered = allResellers.filter(r => followingList.includes(r.resellerId));

        const withFollowers = await Promise.all(
          filtered.map(async (reseller) => {
            try {
              const res = await fetch(`https://reviewhub-lc.onrender.com/api/auth/reseller-followers/${reseller.resellerId}`);
              const data = await res.json();
              return { ...reseller, followerCount: data.followerCount || 0 };
            } catch {
              return { ...reseller, followerCount: 0 };
            }
          })
        );

        setResellers(withFollowers);
      } catch (err) {
        console.error('Error fetching followed resellers:', err);
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchFollowingResellers();
    }
  }, [username]);

  const ResellerCard = ({ reseller }) => {
    const [isFollowing, setIsFollowing] = useState(true); // all are followed initially

    const handleToggleFollow = () => {
      fetch('https://reviewhub-lc.onrender.com/api/auth/toggle-follow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          resellerId: reseller.resellerId,
        }),
      })
        .then(res => res.json())
        .then(data => {
          const updatedFollowing = data.followingResellers || [];
          setFollowingIds(updatedFollowing);
          setIsFollowing(updatedFollowing.includes(reseller.resellerId));

          // remove from list if unfollowed
          if (!updatedFollowing.includes(reseller.resellerId)) {
            setResellers(prev => prev.filter(r => r.resellerId !== reseller.resellerId));
          }
        });
    };

    const handleView = () => {
      navigate(`/reseller/${reseller.resellerId}`);
    };

    return (
     <Paper
  sx={{
    p: isMobile ? 0.5 : 2,
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // ✅ translucent white
    backdropFilter: 'blur(6px)',                // ✅ optional glass blur effect
    borderRadius: 3,
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: isMobile ? 'auto' : 220,
    width: '100%',
    mx: isMobile ? 0 : 1,
    textAlign: isMobile ? 'left' : 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease-in-out',
    animation: 'fadeIn 0.5s ease',
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    },
  }}
  elevation={0} // ✅ remove background elevation color
>

        {/* Avatar */}
<Avatar
  alt={reseller.name}
  src={reseller.profileImageUrl || ''}
  sx={{
    width: isMobile ? 50 : 90,
    height: isMobile ? 50 : 90,
    border: '2px solid #444444',       // thicker pink border for visibility
    borderRadius: '50%',               // enforce circle
    backgroundColor: '#fff',           // in case image doesn't load
    objectFit: 'cover',                // ensure image fills avatar
    outline: 'none',
    boxShadow: 'none',
    '&:focus': { outline: 'none' },
  }}
/>




        {/* Name + Followers */}
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            noWrap
            sx={{ fontSize: isMobile ? '0.95rem' : '1rem' }}
          >
            {reseller.resellerId}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}
          >
            <strong>{reseller.followerCount || 0}</strong> Followers
          </Typography>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 1, }}>
                      <Button
            variant="contained"
            size="small"
            onClick={handleView}
            sx={{
              minWidth: isMobile ? 65 : 80,
              height: 32,
              textTransform: 'none',
                  backgroundColor: '#e6395f',
                      borderRadius: '20px',
            }}
          >
            View
          </Button>
          <Button
             variant="contained"
            size="small"
            color={isFollowing ? 'secondary' : 'primary'}
            onClick={handleToggleFollow}
            sx={{
              minWidth: isMobile ? 65 : 80,
              height: 32,
              textTransform: 'none',
            
                  backgroundColor: '#e6395f',
                      borderRadius: '20px',
            
            }}
          >
            {isFollowing ? 'Unfollow' : 'Follow'}
          </Button>

        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 3, px: 2 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
          color: '#e6395f',
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          textAlign: 'center',
          mb: 3,
          
        }}
      >
        Your Followed Resellers
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, gap: 2 }}>
          {[...Array(5)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={isMobile ? 60 : 260}
              sx={{ borderRadius: 2 }}
            />
          ))}
          <CircularProgress color="primary" sx={{ ml: 2 }} />
        </Box>
      ) : resellers.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          You are not following any resellers yet.
        </Typography>
      ) : (
        <Grid container spacing={isMobile ? 1.5 : 3}>
          {resellers.map((reseller, idx) => (
            <Grid item xs={12} sm={6} md={2.4} key={idx}>
              <ResellerCard reseller={reseller} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default FollowingId;
